import { startTransition, useEffect, useRef, useState, type ReactNode } from "react";
import { observeNear, onIdle } from "@/lib/viewport-anticipation";

/**
 * Defers rendering of children until the placeholder is near the viewport.
 * Holds a min-height to prevent layout shift when children mount.
 *
 * The lookahead band is velocity-aware (see lib/viewport-anticipation): at
 * reading speed it is `rootMarginPx`, during a fling it grows several times
 * larger so the next sections are already mounted by the time they arrive.
 * Mounting happens on an idle callback inside a transition, so it never
 * competes with the scroll.
 *
 * SSR-safe: if IntersectionObserver is unavailable (server, old browsers),
 * children render immediately so SSR HTML stays complete and SEO-friendly.
 */
export function LazyMount({
  children,
  minHeight,
  rootMarginPx = 400,
  className,
  anchorId,
}: {
  children: ReactNode;
  /** Reserve vertical space to avoid CLS while children are unmounted. */
  minHeight: number | string;
  /** Base lookahead in px at reading speed; scaled up while scrolling fast. */
  rootMarginPx?: number;
  className?: string;
  /** Id of an anchor inside children — mount immediately when the URL targets it. */
  anchorId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Always start false on BOTH server and client so the hydrated HTML matches.
  const [mounted, setMounted] = useState(false);

  // Deep links must render their section right away.
  useEffect(() => {
    if (mounted || !anchorId) return;
    const check = () => {
      if (window.location.hash === `#${anchorId}`) setMounted(true);
    };
    check();
    window.addEventListener("hashchange", check);
    return () => window.removeEventListener("hashchange", check);
  }, [mounted, anchorId]);

  useEffect(() => {
    if (mounted) return;
    const el = ref.current;
    if (!el) return;
    return observeNear(el, rootMarginPx, () => {
      onIdle(() => startTransition(() => setMounted(true)), 200);
    });
  }, [mounted, rootMarginPx]);

  return (
    <div
      ref={ref}
      className={className}
      style={mounted ? undefined : { minHeight }}
    >
      {mounted ? children : null}
    </div>
  );
}
